import { UserDto, UserRole, UserStatus } from "@/common/api/datasources/Api";
import { useUsersQuery } from "@/common/api/services/users";
import { getUserRoleTranslationKey, getUserStatusTranslationKey } from "@/common/constants/translations";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Activity, MoreHorizontal, Pencil, Power, Shield, Trash, UserCog } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/common/hooks/useAuth";
import { useAuthQuery } from "@/common/api/services/auth";
import { DeleteUserDialog } from "./DeleteUserDialog";

interface UserActionsProps {
    user: UserDto;
}

export const UserActions = ({ user }: UserActionsProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [selectedStatus, setSelectedStatus] = useState<UserStatus>(user.status as UserStatus);
    const [selectedRole, setSelectedRole] = useState<UserRole>(user.role as UserRole);
    const [hardDelete, setHardDelete] = useState(false);
    const navigate = useNavigate();
    // Отримання аутентифікації
    const { isAuthenticated } = useAuth();
    // Отримання профілю поточного користувача
    const { profileQuery } = useAuthQuery();
    const { data: currentUser } = useQuery(profileQuery);
    const isAdmin = currentUser?.role === UserRole.ADMIN;

    const {
        deleteUserMutation,
        toggleUserActiveMutation,
        changeUserRoleMutation,
        changeUserStatusMutation
    } = useUsersQuery();

    // Мутація для зміни статусу користувача на INACTIVE замість м'якого видалення
    const { mutate: setUserInactive } = useMutation({
        mutationFn: () => changeUserStatusMutation.mutationFn({
            userUUID: user.userUUID,
            status: UserStatus.INACTIVE
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["users"] });
            queryClient.invalidateQueries({ queryKey: ["usersStats"] });
            toast({
                title: t("users.toast.statusChangeSuccess.title"),
                description: t("users.toast.inactiveSuccess.description"),
            });
        },
        onError: () => {
            toast({
                variant: "destructive",
                title: t("users.toast.statusChangeError.title"),
                description: t("users.toast.statusChangeError.description"),
            });
        },
    });

    // Мутація для видалення
    const { mutate: deleteUser } = useMutation({
        mutationFn: () => deleteUserMutation.mutationFn(user.userUUID, hardDelete),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["users"] });
            queryClient.invalidateQueries({ queryKey: ["usersStats"] });
            toast({
                title: t("users.toast.deleteSuccess.title"),
                description: hardDelete
                    ? t("users.toast.hardDeleteSuccess.description")
                    : t("users.toast.deleteSuccess.description"),
            });
        },
        onError: () => {
            toast({
                variant: "destructive",
                title: t("users.toast.deleteError.title"),
                description: t("users.toast.deleteError.description"),
            });
        },
    });

    // Мутація для зміни активності
    const { mutate: toggleActive } = useMutation({
        mutationFn: () => toggleUserActiveMutation.mutationFn({
            userUUID: user.userUUID,
            isActive: !user.isActive
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["users"] });
            queryClient.invalidateQueries({ queryKey: ["usersStats"] });
            toast({
                title: t(user.isActive ? "users.toast.deactivateSuccess.title" : "users.toast.activateSuccess.title"),
                description: t(user.isActive ? "users.toast.deactivateSuccess.description" : "users.toast.activateSuccess.description"),
            });
        },
        onError: () => {
            toast({
                variant: "destructive",
                title: t("users.toast.updateError.title"),
                description: t("users.toast.updateError.description"),
            });
        },
    });

    // Мутація для зміни ролі
    const { mutate: changeRole } = useMutation({
        mutationFn: (role: UserRole) => changeUserRoleMutation.mutationFn({
            userUUID: user.userUUID,
            role
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["users"] });
            queryClient.invalidateQueries({ queryKey: ["usersStats"] });
            toast({
                title: t("users.toast.roleUpdateSuccess.title"),
                description: t("users.toast.roleUpdateSuccess.description"),
            });
        },
        onError: () => {
            toast({
                variant: "destructive",
                title: t("users.toast.updateError.title"),
                description: t("users.toast.updateError.description"),
            });
        },
    });

    // Мутація для зміни статусу
    const { mutate: changeStatus } = useMutation({
        mutationFn: (status: UserStatus) => changeUserStatusMutation.mutationFn({
            userUUID: user.userUUID,
            status
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["users"] });
            queryClient.invalidateQueries({ queryKey: ["usersStats"] });
            toast({
                title: t("users.toast.statusUpdateSuccess.title"),
                description: t("users.toast.statusUpdateSuccess.description"),
            });
        },
        onError: () => {
            toast({
                variant: "destructive",
                title: t("users.toast.updateError.title"),
                description: t("users.toast.updateError.description"),
            });
        },
    });

    const handleStatusChange = () => {
        if (selectedStatus !== user.status) {
            changeStatus(selectedStatus);
        }
    };

    const handleRoleChange = () => {
        if (selectedRole !== user.role) {
            changeRole(selectedRole);
        }
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    className="h-8 w-8 p-0"
                    aria-label={t("users.actions.open")}
                >
                    <MoreHorizontal className="h-4 w-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate(`/settings/${user.userUUID}`)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    {t("users.actions.edit")}
                </DropdownMenuItem>

                {/* Зміна ролі */}
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <DropdownMenuItem
                            className="cursor-pointer"
                            onSelect={(e) => e.preventDefault()}
                        >
                            <Shield className="mr-2 h-4 w-4" />
                            {t("users.actions.changeRole")}
                        </DropdownMenuItem>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>{t("users.role.title")}</AlertDialogTitle>
                            <AlertDialogDescription>
                                {t("users.role.description")}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <div className="py-4">
                            <Select
                                defaultValue={user.role}
                                onValueChange={(value: UserRole) => {
                                    setSelectedRole(value);
                                }}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={t("users.role.options.unassigned")} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={UserRole.ADMIN}>
                                        {t(getUserRoleTranslationKey(UserRole.ADMIN))}
                                    </SelectItem>
                                    <SelectItem value={UserRole.OWNER}>
                                        {t(getUserRoleTranslationKey(UserRole.OWNER))}
                                    </SelectItem>
                                    <SelectItem value={UserRole.STAFF}>
                                        {t(getUserRoleTranslationKey(UserRole.STAFF))}
                                    </SelectItem>
                                    <SelectItem value={UserRole.TENANT}>
                                        {t(getUserRoleTranslationKey(UserRole.TENANT))}
                                    </SelectItem>
                                    <SelectItem value={UserRole.UNASSIGNED}>
                                        {t(getUserRoleTranslationKey(UserRole.UNASSIGNED))}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <AlertDialogFooter>
                            <AlertDialogCancel>
                                {t("users.role.cancel")}
                            </AlertDialogCancel>
                            <AlertDialogAction onClick={handleRoleChange}>
                                {t("users.role.confirm")}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Зміна статусу */}
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <DropdownMenuItem
                            className="cursor-pointer"
                            onSelect={(e) => e.preventDefault()}
                        >
                            <Activity className="mr-2 h-4 w-4" />
                            {t("users.actions.changeStatus")}
                        </DropdownMenuItem>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>{t("users.status.title")}</AlertDialogTitle>
                            <AlertDialogDescription>
                                {t("users.status.description")}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <div className="py-4">
                            <Select
                                defaultValue={user.status}
                                onValueChange={(value: UserStatus) => {
                                    setSelectedStatus(value);
                                }}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={t("users.status.options.pending")} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={UserStatus.ACTIVE}>
                                        {t(getUserStatusTranslationKey(UserStatus.ACTIVE))}
                                    </SelectItem>
                                    <SelectItem value={UserStatus.PENDING}>
                                        {t(getUserStatusTranslationKey(UserStatus.PENDING))}
                                    </SelectItem>
                                    <SelectItem value={UserStatus.INACTIVE}>
                                        {t(getUserStatusTranslationKey(UserStatus.INACTIVE))}
                                    </SelectItem>
                                    <SelectItem value={UserStatus.SUSPENDED}>
                                        {t(getUserStatusTranslationKey(UserStatus.SUSPENDED))}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <AlertDialogFooter>
                            <AlertDialogCancel>
                                {t("users.status.cancel")}
                            </AlertDialogCancel>
                            <AlertDialogAction onClick={handleStatusChange}>
                                {t("users.status.confirm")}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Активація/Деактивація */}
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <DropdownMenuItem
                            className="cursor-pointer"
                            onSelect={(e) => e.preventDefault()}
                        >
                            <Power className="mr-2 h-4 w-4" />
                            {user.isActive ? t("users.actions.deactivate") : t("users.actions.activate")}
                        </DropdownMenuItem>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>
                                {t(user.isActive ? "users.deactivate.title" : "users.activate.title")}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                                {t(user.isActive ? "users.deactivate.description" : "users.activate.description")}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>
                                {t(user.isActive ? "users.deactivate.cancel" : "users.activate.cancel")}
                            </AlertDialogCancel>
                            <AlertDialogAction
                                onClick={() => toggleActive()}
                            >
                                {t(user.isActive ? "users.deactivate.confirm" : "users.activate.confirm")}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                <DropdownMenuSeparator />

                {/* Видалення - заміняємо на новий компонент */}
                <DeleteUserDialog
                    user={user}
                    trigger={
                        <DropdownMenuItem
                            className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                            onSelect={(e) => e.preventDefault()}
                        >
                            <Trash className="mr-2 h-4 w-4" />
                            {t("users.actions.delete")}
                        </DropdownMenuItem>
                    }
                />
            </DropdownMenuContent>
        </DropdownMenu>
    );
};